import { apiRequest } from '../../../shared/services/apiClient';

export type McpDiscoveredTool = {
  name: string;
  description: string;
  inputSchema?: unknown;
  readOnlyHint?: boolean | null;
};

export type McpCatalogField = {
  name: string;
  placeholder: string;
  allowedValues: string[];
};

export type McpCatalogProvider = {
  providerId: string;
  displayName: string;
  description: string;
  endpointTemplate: string;
  transport: string;
  authMode: string;
  supportsPersonalAccessToken: boolean;
  fields: McpCatalogField[];
};

export type McpCatalogConnectResult = {
  serverId: string;
  authorizeUrl: string;
  requiresPersonalAccessToken: boolean;
  requiresOAuthClient: boolean;
  oauthRedirectUri: string;
  connected: boolean;
  error: string;
};

export type McpServer = {
  serverId: string;
  displayName: string;
  description: string;
  endpointUrl: string;
  transport: string;
  authType: string;
  authHeaderName: string;
  authConfigured: boolean;
  catalogProviderId: string;
  oauthGrantType: string;
  oauthAuthorizationUrl: string;
  oauthTokenUrl: string;
  oauthScopes: string;
  oauthClientId: string;
  oauthClientSecretConfigured: boolean;
  oauthConnected: boolean;
  lastDiscoveredAt?: string;
  lastTestStatus?: string;
  lastTestMessage?: string;
  discoveredTools: McpDiscoveredTool[];
};

export type McpServerWritePayload = {
  displayName: string;
  description?: string;
  endpointUrl: string;
  transport?: string;
  authType?: string;
  authHeaderName?: string;
  authValue?: string;
  clearAuthValue?: boolean;
  oauthGrantType?: string;
  oauthAuthorizationUrl?: string;
  oauthTokenUrl?: string;
  oauthScopes?: string;
  oauthClientId?: string;
  oauthClientSecret?: string;
  clearOAuthClientSecret?: boolean;
  clearOAuthTokens?: boolean;
};

export type McpToolParameter = {
  name: string;
  description: string;
  isRequired: boolean;
  defaultValue: string;
  example: string;
};

export type McpTool = {
  toolId: string;
  serverId: string;
  remoteToolName: string;
  kernelFunctionName: string;
  displayName: string;
  description: string;
  inputSchema?: unknown;
  parameters: McpToolParameter[];
  isPublished: boolean;
  remoteToolHash: string;
  functionId: string;
};

export type McpConnectionTestResult = {
  serverId: string;
  success: boolean;
  protocolVersion?: string;
  serverName?: string;
  serverVersion?: string;
  toolCount: number;
  error?: string;
  durationMs: number;
};

export type McpDiscoveryResult = {
  serverId: string;
  protocolVersion?: string;
  serverName?: string;
  serverVersion?: string;
  tools: McpDiscoveredTool[];
  discoveredAt: string;
};

const normalizeServer = (value: any): McpServer => ({
  serverId: String(value?.serverId ?? ''),
  displayName: String(value?.displayName ?? ''),
  description: String(value?.description ?? ''),
  endpointUrl: String(value?.endpointUrl ?? ''),
  transport: String(value?.transport ?? 'streamableHttp'),
  authType: String(value?.authType ?? 'none'),
  authHeaderName: String(value?.authHeaderName ?? 'X-Api-Key'),
  authConfigured: Boolean(value?.authConfigured),
  catalogProviderId: String(value?.catalogProviderId ?? ''),
  oauthGrantType: String(value?.oauthGrantType ?? 'authorizationCode'),
  oauthAuthorizationUrl: String(value?.oauthAuthorizationUrl ?? ''),
  oauthTokenUrl: String(value?.oauthTokenUrl ?? ''),
  oauthScopes: String(value?.oauthScopes ?? ''),
  oauthClientId: String(value?.oauthClientId ?? ''),
  oauthClientSecretConfigured: Boolean(value?.oauthClientSecretConfigured),
  oauthConnected: Boolean(value?.oauthConnected),
  lastDiscoveredAt: value?.lastDiscoveredAt ? String(value.lastDiscoveredAt) : undefined,
  lastTestStatus: value?.lastTestStatus ? String(value.lastTestStatus) : undefined,
  lastTestMessage: value?.lastTestMessage ? String(value.lastTestMessage) : undefined,
  discoveredTools: Array.isArray(value?.discoveredTools)
    ? value.discoveredTools.map((tool: any) => ({
      name: String(tool?.name ?? ''),
      description: String(tool?.description ?? ''),
      inputSchema: tool?.inputSchema,
      readOnlyHint: typeof tool?.readOnlyHint === 'boolean' ? tool.readOnlyHint : null,
    }))
    : [],
});

const normalizeTool = (value: any): McpTool => ({
  toolId: String(value?.toolId ?? ''),
  serverId: String(value?.serverId ?? ''),
  remoteToolName: String(value?.remoteToolName ?? ''),
  kernelFunctionName: String(value?.kernelFunctionName ?? ''),
  displayName: String(value?.displayName ?? ''),
  description: String(value?.description ?? ''),
  inputSchema: value?.inputSchema,
  parameters: Array.isArray(value?.parameters)
    ? value.parameters.map((parameter: any) => ({
      name: String(parameter?.name ?? ''),
      description: String(parameter?.description ?? ''),
      isRequired: Boolean(parameter?.isRequired),
      defaultValue: String(parameter?.defaultValue ?? ''),
      example: String(parameter?.example ?? ''),
    }))
    : [],
  isPublished: Boolean(value?.isPublished),
  remoteToolHash: String(value?.remoteToolHash ?? ''),
  functionId: String(value?.functionId ?? `mcp.${value?.toolId ?? ''}`),
});

export const listMcpServers = async (): Promise<McpServer[]> => {
  const result = await apiRequest<any[]>('/api/v1/ai/mcp/servers');
  return Array.isArray(result) ? result.map(normalizeServer) : [];
};

export const listMcpCatalog = async (): Promise<McpCatalogProvider[]> => {
  const result = await apiRequest<any[]>('/api/v1/ai/mcp/catalog');
  return Array.isArray(result)
    ? result.map((value: any) => ({
      providerId: String(value?.providerId ?? ''),
      displayName: String(value?.displayName ?? ''),
      description: String(value?.description ?? ''),
      endpointTemplate: String(value?.endpointTemplate ?? ''),
      transport: String(value?.transport ?? 'streamableHttp'),
      authMode: String(value?.authMode ?? 'mcpOAuth'),
      supportsPersonalAccessToken: Boolean(value?.supportsPersonalAccessToken),
      fields: Array.isArray(value?.fields)
        ? value.fields.map((field: any) => ({
          name: String(field?.name ?? ''),
          placeholder: String(field?.placeholder ?? ''),
          allowedValues: Array.isArray(field?.allowedValues)
            ? field.allowedValues.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
            : [],
        }))
        : [],
    }))
    : [];
};

export const connectMcpCatalogProvider = async (
  providerId: string,
  payload: {
    fields?: Record<string, string>;
    personalAccessToken?: string;
    oauthClientId?: string;
    oauthClientSecret?: string;
  } = {},
): Promise<McpCatalogConnectResult> => {
  const result = await apiRequest<any>(
    `/api/v1/ai/mcp/catalog/${encodeURIComponent(providerId)}/connect`,
    {
      method: 'POST',
      body: JSON.stringify({
        fields: payload.fields,
        personalAccessToken: payload.personalAccessToken,
        oauthClientId: payload.oauthClientId,
        oauthClientSecret: payload.oauthClientSecret,
      }),
    },
  );

  return {
    serverId: String(result?.serverId ?? ''),
    authorizeUrl: String(result?.authorizeUrl ?? ''),
    requiresPersonalAccessToken: Boolean(result?.requiresPersonalAccessToken),
    requiresOAuthClient: Boolean(result?.requiresOAuthClient),
    oauthRedirectUri: String(result?.oauthRedirectUri ?? ''),
    connected: Boolean(result?.connected),
    error: String(result?.error ?? ''),
  };
};

const buildAuthPayload = (form: McpServerWritePayload) => ({
  authType: form.authType,
  authHeaderName: form.authHeaderName,
  authValue: form.authType === 'oauth' ? undefined : form.authValue,
  clearAuthValue: form.clearAuthValue,
  oauthGrantType: form.authType === 'oauth' ? form.oauthGrantType : undefined,
  oauthAuthorizationUrl: form.authType === 'oauth' ? form.oauthAuthorizationUrl?.trim() : undefined,
  oauthTokenUrl: form.authType === 'oauth' ? form.oauthTokenUrl?.trim() : undefined,
  oauthScopes: form.authType === 'oauth' ? form.oauthScopes?.trim() : undefined,
  oauthClientId: form.authType === 'oauth' ? form.oauthClientId?.trim() : undefined,
  oauthClientSecret: form.authType === 'oauth' ? form.oauthClientSecret?.trim() || undefined : undefined,
  clearOAuthClientSecret: form.clearOAuthClientSecret,
  clearOAuthTokens: form.clearOAuthTokens,
});

export const createMcpServer = async (payload: McpServerWritePayload): Promise<McpServer> => {
  const result = await apiRequest<any>('/api/v1/ai/mcp/servers', {
    method: 'POST',
    body: JSON.stringify({
      displayName: payload.displayName,
      description: payload.description,
      endpointUrl: payload.endpointUrl,
      transport: payload.transport,
      ...buildAuthPayload(payload),
    }),
  });
  return normalizeServer(result);
};

export const updateMcpServer = async (
  serverId: string,
  payload: McpServerWritePayload,
): Promise<McpServer> => {
  const result = await apiRequest<any>(`/api/v1/ai/mcp/servers/${encodeURIComponent(serverId)}`, {
    method: 'PUT',
    body: JSON.stringify({
      displayName: payload.displayName,
      description: payload.description,
      endpointUrl: payload.endpointUrl,
      transport: payload.transport,
      ...buildAuthPayload(payload),
    }),
  });
  return normalizeServer(result);
};

export const startMcpOAuth = async (serverId: string): Promise<string> => {
  const result = await apiRequest<{ authorizeUrl?: string }>(
    `/api/v1/ai/mcp/servers/${encodeURIComponent(serverId)}/oauth/start`,
    { method: 'POST' },
  );
  return String(result?.authorizeUrl ?? '');
};

export const disconnectMcpOAuth = async (serverId: string): Promise<void> => {
  await apiRequest(`/api/v1/ai/mcp/servers/${encodeURIComponent(serverId)}/oauth`, {
    method: 'DELETE',
  });
};

export const deleteMcpServer = async (serverId: string): Promise<void> => {
  await apiRequest<void>(`/api/v1/ai/mcp/servers/${encodeURIComponent(serverId)}`, {
    method: 'DELETE',
  });
};

export const testMcpServer = async (serverId: string): Promise<McpConnectionTestResult> => {
  return apiRequest<McpConnectionTestResult>(`/api/v1/ai/mcp/servers/${encodeURIComponent(serverId)}/test`, {
    method: 'POST',
  });
};

export const discoverMcpServer = async (serverId: string): Promise<McpDiscoveryResult> => {
  return apiRequest<McpDiscoveryResult>(`/api/v1/ai/mcp/servers/${encodeURIComponent(serverId)}/discover`, {
    method: 'POST',
  });
};

export const importMcpTools = async (
  serverId: string,
  remoteToolNames: string[],
  isPublished?: boolean,
  assignedUseCaseIds?: string[],
): Promise<McpTool[]> => {
  const result = await apiRequest<any[]>(`/api/v1/ai/mcp/servers/${encodeURIComponent(serverId)}/import-tools`, {
    method: 'POST',
    body: JSON.stringify({ remoteToolNames, isPublished, assignedUseCaseIds }),
  });
  return Array.isArray(result) ? result.map(normalizeTool) : [];
};

export const listMcpTools = async (serverId?: string): Promise<McpTool[]> => {
  const query = serverId ? `?serverId=${encodeURIComponent(serverId)}` : '';
  const result = await apiRequest<any[]>(`/api/v1/ai/mcp/tools${query}`);
  return Array.isArray(result) ? result.map(normalizeTool) : [];
};

export const updateMcpTool = async (
  toolId: string,
  payload: {
    kernelFunctionName: string;
    displayName: string;
    description: string;
    isPublished: boolean;
    assignedUseCaseIds?: string[];
  },
): Promise<McpTool> => {
  const result = await apiRequest<any>(`/api/v1/ai/mcp/tools/${encodeURIComponent(toolId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return normalizeTool(result);
};

export const deleteMcpTool = async (toolId: string): Promise<void> => {
  await apiRequest<void>(`/api/v1/ai/mcp/tools/${encodeURIComponent(toolId)}`, {
    method: 'DELETE',
  });
};

export const previewMcpTool = async (
  toolId: string,
  argumentsMap: Record<string, string>,
): Promise<string> => {
  const result = await apiRequest<{ result: string }>(`/api/v1/ai/mcp/tools/${encodeURIComponent(toolId)}/preview`, {
    method: 'POST',
    body: JSON.stringify({ arguments: argumentsMap }),
  });
  return String(result?.result ?? '');
};
